import React from 'react';
import styled, { css } from 'styled-components';
import { DateRangeInput, SortFieldAdvanced } from '~/graphql/types';

import TEST_ID from './index.testid';
import Dropdown, { OptionOf } from '~/components/molecule/Dropdown';
import { Heading4, Variant } from '~/components/atom/Typography';
import useTrackEvent from '~/hooks/useTrackEvent';
import JustificationContainer from '~/components/atom/JustificationContainer';

export type Props = {
  selectedOptionIndex: number;
  options: Array<OptionOf<DateRangeInput | SortFieldAdvanced | number | null>>;
  onOptionChange: ({
    selectedOptionIdx,
  }: {
    selectedOptionIdx: number;
  }) => void;
  headingText: string;
};

const CardHeader: React.FCC<Props> = ({
  dataTestId,
  selectedOptionIndex,
  options,
  onOptionChange,
  headingText,
}) => {
  const trackEvent = useTrackEvent();

  const handleOptionChange = ({
    selectedOptionIdx,
  }: {
    selectedOptionIdx: number;
  }) => {
    /** ----- TRACKING: dashboard_widget_interacted ----- */
    trackEvent({
      eventName: 'dashboard_widget_interacted',
      parameters: {
        widget_type: headingText.toLowerCase().replace(/\s+/g, '_'),
        interaction_type: 'filter_change',
      },
    });
    onOptionChange({ selectedOptionIdx });
  };

  return (
    <Container
      dataTestId={`${dataTestId}-${TEST_ID.CONTAINER}`}
      justification="space-between"
      margin={[null, null, 'l', null]}
      align="center"
      gap="xxs"
    >
      <Heading4 margin={[null]} variant={Variant.primary}>
        {headingText}
      </Heading4>
      <DropdownRestriction>
        <Dropdown
          dataTestId={`${dataTestId}-${TEST_ID.DROPDOWN}`}
          options={options}
          selectedOptionIdx={selectedOptionIndex}
          onChange={handleOptionChange}
        />
      </DropdownRestriction>
    </Container>
  );
};

const Container = styled(JustificationContainer)<{}>(
  ({ theme }) => css`
    margin-bottom: ${theme.space('l')};

    ${theme.mq.lessThan('mobile')`
      flex-direction: column;
      align-items: flex-start;
      gap: ${theme.space('xs')};
    `}
  `,
);

const DropdownRestriction = styled.div<{}>(
  ({ theme }) => css`
    flex: 0 1 50%;

    ${theme.mq.lessThan('mobile')`
      flex: 1 1 100%;
      width: 100%;
    `}
  `,
);

export default CardHeader;
